import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

// Tier 1 (Vik 2026-11-02): the invite link carries its token in the URL fragment (#t=...), which the browser never sends to a
// server or in a Referer header. It is read once and removed from the address bar.
function readFragmentToken() {
  const m = /(?:^|[#&])t=([^&]+)/.exec(window.location.hash || '');
  if (!m) return null;
  window.history.replaceState(null, '', window.location.pathname);
  return decodeURIComponent(m[1]);
}

const rules = [
  { label: 'At least 12 characters',                    test: p => p.length >= 12 },
  { label: 'One uppercase letter (A-Z)',                 test: p => /[A-Z]/.test(p) },
  { label: 'One lowercase letter (a-z)',                 test: p => /[a-z]/.test(p) },
  { label: 'One number (0-9)',                           test: p => /\d/.test(p) },
  { label: 'One special character (!@#$…)',              test: p => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
  { label: 'Not one of your last 10 passwords',         test: () => true }, // enforced server-side only
];

function PolicyChecker({ password }) {
  return (
    <ul className="space-y-1 mt-2">
      {rules.map(r => {
        const passed = r.test(password);
        return (
          <li key={r.label} className={`flex items-center gap-2 text-xs ${passed ? 'text-green-600' : 'text-gray-400'}`}>
            <span>{passed ? '✓' : '○'}</span>
            {r.label}
          </li>
        );
      })}
    </ul>
  );
}

export default function SetupPassword() {
  const navigate = useNavigate();
  const [token] = useState(readFragmentToken);
  const [loginPath, setLoginPath] = useState('/login');
  const [doneMessage, setDoneMessage] = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Invalid Link</h2>
          <p className="text-gray-500">This link is invalid or has expired; ask your administrator for a new one.</p>
        </div>
      </div>
    );
  }

  const allRulesPassed = rules.every(r => r.test(password));

  const handleSubmit = async () => {
    setError('');
    if (!allRulesPassed) {
      setError('Password does not meet the security requirements below.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/setup-password', { token, password });
      const next = res?.data?.login_path || '/login';
      setLoginPath(next);
      // The server says what comes next: MFA set-up for a new user, or the existing authenticator after a reset.
      setDoneMessage(res?.data?.message || 'Password set. Sign in next.');
      setDone(true);
      setTimeout(() => navigate(next), 3000);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <div className="text-4xl mb-4">✅</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Password Set!</h2>
          <p className="text-gray-500">{doneMessage} Redirecting to {loginPath}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white rounded-xl shadow p-8 max-w-md w-full">
        <div className="mb-6 text-center">
          <div className="text-4xl mb-3">🔐</div>
          <h2 className="text-2xl font-bold text-gray-900">Set Your Password</h2>
          <p className="text-gray-500 mt-1 text-sm">Choose a strong password to activate your account. </p>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter a strong password"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {/* Live policy checker */}
            {password.length > 0 && <PolicyChecker password={password} />}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {confirm.length > 0 && password !== confirm && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match.</p>
            )}
            {confirm.length > 0 && password === confirm && (
              <p className="text-xs text-green-600 mt-1">✓ Passwords match.</p>
            )}
          </div>
          <button
            onClick={handleSubmit}
            disabled={loading || !allRulesPassed || password !== confirm}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg text-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Setting password...' : 'Set Password & Activate Account'}
          </button>
        </div>
      </div>
    </div>
  );
}